"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, ChartBar as BarChart3, SlidersHorizontal as Sliders, CircleCheck as CheckCircle2, Lock, Globe, Settings2, RefreshCw } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface CookieCategory {
  id: string;
  title: string;
  categoryTag: string;
  badge: string;
  isMandatory: boolean;
  description: string;
  storagePeriod: string;
  cookiesList: string;
  examples: string;
}
const cookieCategories: CookieCategory[] = [{
  id: "essential",
  title: "Essential & Security Cookies",
  categoryTag: "Strictly Necessary",
  badge: "Always active",
  isMandatory: true,
  description: "These files maintain your secure session, preserve your reading preferences, and keep digital authentication tokens safe. The site cannot function properly without them.",
  storagePeriod: "Session to 12 months",
  cookiesList: "minfo_session, csrf_token, auth_state, consent_preferences",
  examples: "Session validation, CSRF attack defense, accessibility settings"
}, {
  id: "analytical",
  title: "Analytical & Performance Metrics",
  categoryTag: "Performance",
  badge: "Optional",
  isMandatory: false,
  description: "Aggregated, non-personally identifiable telemetry that reveals which research papers, briefs, and editorial reports readers access most frequently.",
  storagePeriod: "14 months maximum",
  cookiesList: "_ga, _gid, minfo_analytics_id, report_view_counter",
  examples: "Page load metrics, reading time telemetry, report popularity"
}, {
  id: "functional",
  title: "Preference & Resource Tools",
  categoryTag: "Functionality",
  badge: "Optional",
  isMandatory: false,
  description: "Remembers personal reading interface settings, preferred font sizing, bookmark queues for long-form whitepapers, and downloaded report history.",
  storagePeriod: "6 to 24 months",
  cookiesList: "font_scale_pref, library_bookmarks, recent_research_queue",
  examples: "Saved bookmarks, custom display scale, reading progress markers"
}];
interface BrowserGuide {
  name: string;
  instructions: string;
  supportUrl: string;
}
const browserGuides: BrowserGuide[] = [{
  name: "Google Chrome",
  instructions: "Open Settings > Privacy and Security > Third-party cookies.",
  supportUrl: "https://support.google.com/chrome/answer/95647"
}, {
  name: "Apple Safari",
  instructions: "Open Preferences / Settings > Privacy > Manage Website Data.",
  supportUrl: "https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac"
}, {
  name: "Mozilla Firefox",
  instructions: "Open Settings > Privacy & Security > Cookies and Site Data.",
  supportUrl: "https://support.mozilla.org/kb/enhanced-tracking-protection-firefox-desktop"
}, {
  name: "Microsoft Edge",
  instructions: "Open Settings > Cookies and site permissions > Manage cookies.",
  supportUrl: "https://support.microsoft.com/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
}];
export default function CookiePolicy() {
  const shouldReduceMotion = useReducedMotion();
  const [selectedCategory, setSelectedCategory] = useState<string>("essential");
  const [analyticsEnabled, setAnalyticsEnabled] = useState<boolean>(true);
  const [functionalEnabled, setFunctionalEnabled] = useState<boolean>(true);
  const [savedSuccess, setSavedSuccess] = useState<boolean>(false);
  const handleSavePreferences = () => {
    setSavedSuccess(true);
    setTimeout(() => {
      setSavedSuccess(false);
    }, 3000);
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1] as const
      }
    }
  };
  return <section id="cookie-policy" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        {/* Masthead rule divider */}
        <div className="mb-10 sm:mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-[1px] w-8 bg-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-sans">
              Disclosure & Technical Standards
            </span>
          </div>
          
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 pb-6 border-b border-border">
            <div className="max-w-3xl">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight leading-tight text-foreground">
                How we use cookies and local storage
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                MinfoMedia employs minimal data storage files to keep editorial workflows secure, preserve reading preferences, and measure report performance. We do not sell tracking profiles to third-party ad exchanges.
              </p>
            </div>
            
            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground bg-muted/60 px-3.5 py-2 rounded-sm border border-border shrink-0 self-start lg:self-auto">
              <Lock className="w-3.5 h-3.5 text-foreground" />
              <span>Policy revision: Current publication cycle</span>
            </div>
          </div>
          <div className="h-[0.5px] w-full bg-border/60 mt-[2px]" />
        </div>

        {/* Three Category Cards with Stagger Reveal */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {cookieCategories.map((item, index) => {
          const isSelected = selectedCategory === item.id;
          return <motion.div key={item.id} variants={itemVariants} data-index={index} className="h-full">
                <Card onClick={() => setSelectedCategory(item.id)} className={`h-full flex flex-col justify-between border cursor-pointer transition-[border-color,box-shadow] duration-200 rounded-md bg-card ${isSelected ? "border-foreground shadow-md ring-1 ring-ring" : "border-border shadow-xs hover:border-foreground/40 hover:shadow"}`}>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="text-[11px] font-mono tracking-normal text-muted-foreground uppercase bg-muted px-2 py-0.5 rounded-sm border border-border">
                        {item.categoryTag}
                      </span>
                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${item.isMandatory ? "bg-secondary text-secondary-foreground font-semibold" : "bg-muted text-muted-foreground"}`}>
                        {item.badge}
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-sm bg-muted text-foreground shrink-0 border border-border">
                        {index === 0 && <ShieldCheck className="w-5 h-5" />}
                        {index === 1 && <BarChart3 className="w-5 h-5" />}
                        {index === 2 && <Sliders className="w-5 h-5" />}
                      </div>
                      <CardTitle className="text-xl font-serif font-semibold text-card-foreground">
                        {item.title}
                      </CardTitle>
                    </div>

                    <CardDescription className="text-sm text-muted-foreground leading-relaxed mt-3">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-3 pt-2 text-xs">
                    <div className="p-3 bg-muted/40 rounded-sm border border-border space-y-2">
                      <div>
                        <span className="font-semibold text-foreground block font-sans mb-0.5">
                          Identified keys:
                        </span>
                        <code className="text-[11px] font-mono text-muted-foreground break-all">
                          {item.cookiesList}
                        </code>
                      </div>
                      <div className="pt-2 border-t border-border/70 flex justify-between items-center text-muted-foreground">
                        <span>Retention window:</span>
                        <span className="font-mono text-foreground font-medium">
                          {item.storagePeriod}
                        </span>
                      </div>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2 border-t border-border mt-auto">
                    <div className="w-full flex items-center justify-between text-xs text-muted-foreground">
                      <span className="font-sans">Key function:</span>
                      <span className="font-medium text-foreground truncate max-w-[180px] text-right">
                        {item.examples}
                      </span>
                    </div>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Preference Control Workspace */}
        <div className="bg-card border border-border rounded-md p-6 sm:p-8 shadow-xs mb-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-border">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                <Settings2 className="w-4 h-4 text-foreground" />
                <span>Reader Consent Panel</span>
              </div>
              <h3 className="text-2xl font-serif font-semibold text-foreground">
                Manage your storage preferences
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Select which non-essential technologies MinfoMedia may deploy on this browser.
              </p>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              {savedSuccess && <span className="text-xs font-medium text-foreground flex items-center gap-1.5 bg-muted px-3 py-2 rounded-sm border border-border">
                  <CheckCircle2 className="w-4 h-4 text-foreground" />
                  Preferences updated
                </span>}
              <Button variant="default" size="default" onClick={handleSavePreferences} className="font-medium cursor-pointer">
                <RefreshCw className="w-4 h-4 mr-1.5" />
                Save choices
              </Button>
            </div>
          </div>

          <div className="divide-y divide-border">
            {/* Essential Row */}
            <div className="py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="max-w-2xl">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-base text-foreground font-sans">
                    Strictly necessary storage
                  </span>
                  <span className="text-[10px] uppercase font-mono px-2 py-0.5 bg-muted text-muted-foreground rounded-sm border border-border">
                    Required
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Required for user authentication, fraud prevention, and session continuity. Cannot be disabled.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-muted-foreground">Always active</span>
                <div className="w-11 h-6 bg-primary rounded-full relative p-0.5 flex items-center justify-end opacity-90 cursor-not-allowed">
                  <div className="w-5 h-5 rounded-full bg-primary-foreground shadow-xs" />
                </div>
              </div>
            </div>

            {/* Analytics Row */}
            <div className="py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="max-w-2xl">
                <span className="font-medium text-base text-foreground font-sans">
                  Performance & report engagement analytics
                </span>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Enables anonymous measurement of article engagement, download volumes, and site performance.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant={analyticsEnabled ? "default" : "outline"} size="sm" onClick={() => setAnalyticsEnabled(!analyticsEnabled)} className="font-mono text-xs cursor-pointer min-w-[70px]">
                  {analyticsEnabled ? "Enabled" : "Disabled"}
                </Button>
              </div>
            </div>

            {/* Functional Row */}
            <div className="py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="max-w-2xl">
                <span className="font-medium text-base text-foreground font-sans">
                  Customization & library state
                </span>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Remembers your font scale, bookmarked whitepapers, and preferred reading layout between sessions.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant={functionalEnabled ? "default" : "outline"} size="sm" onClick={() => setFunctionalEnabled(!functionalEnabled)} className="font-mono text-xs cursor-pointer min-w-[70px]">
                  {functionalEnabled ? "Enabled" : "Disabled"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Browser Settings Instructions Section */}
        <div className="border border-border bg-card rounded-md p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 pb-6 border-b border-border">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                <Globe className="w-4 h-4 text-foreground" />
                <span>Browser Level Configuration</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-serif font-semibold text-foreground">
                Managing storage through your browser
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground max-w-md">
              You can block or purge cookies directly through your web browser preferences at any time.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {browserGuides.map((guide, idx) => <div key={guide.name} data-index={idx} className="p-4 rounded-sm border border-border bg-muted/30 flex flex-col justify-between space-y-3">
                <div>
                  <h4 className="font-serif font-semibold text-base text-foreground mb-1.5">
                    {guide.name}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {guide.instructions}
                  </p>
                </div>

                <div className="pt-2 border-t border-border/60">
                  <Link to={guide.supportUrl} newTab className="inline-flex items-center text-xs font-medium text-foreground hover:underline">
                    <span>Official instructions</span>
                  </Link>
                </div>
              </div>)}
          </div>

          {/* Privacy Officer Contact strip */}
          <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs text-muted-foreground">
            <p>
              Questions regarding our data retention practices? Contact our compliance desk at{" "}
              <a href="mailto:info@minfomedia.com" className="font-medium text-foreground underline underline-offset-4">
                info@minfomedia.com
              </a>
              .
            </p>

            <div className="flex items-center gap-4 shrink-0">
              <Link to="/TermsConditionsPage" className="hover:text-foreground transition-colors underline-offset-4 hover:underline">
                Terms & Conditions
              </Link>
              <span>•</span>
              <Link to="/PrivacyDataProtectionPage" className="hover:text-foreground transition-colors underline-offset-4 hover:underline">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}